import { defineStore } from "pinia";

const LOCATION_PERMISSION_KEY = "locationPermission";
const LOCATION_DATA_KEY = "locationData";
const LOCATION_DIAGNOSTICS_KEY = "locationDiagnostics";
const LOCATION_DIAGNOSTIC_LIMIT = 20;
const GEOLOCATION_OPTIONS = {
  enableHighAccuracy: true,
  maximumAge: 0,
  timeout: 12000,
} as const;

type LocationPermission = "allowed" | "denied" | null;
type LocationSource = "browser" | "ip-fallback" | "manual" | "unknown";
type GeolocationRequestOptions = {
  fallbackToIp?: boolean;
};

interface LocationData {
  approxRegion: string;
  latitude: number | null;
  longitude: number | null;
  city: string;
  country: string;
  source: LocationSource;
  accuracy: number | null;
  timestamp: number;
}

interface LocationDiagnostic {
  phase:
    | "cache-restored"
    | "request-start"
    | "browser-success"
    | "browser-error"
    | "browser-unavailable"
    | "fallback-start"
    | "fallback-success"
    | "fallback-error";
  timestamp: string;
  permission: LocationPermission;
  source?: LocationSource;
  lat?: number | null;
  lon?: number | null;
  accuracy?: number | null;
  errorCode?: number;
  errorMessage?: string;
  highAccuracy?: boolean;
  maximumAgeMs?: number;
  timeoutMs?: number;
  secureContext?: boolean;
}

declare global {
  interface Window {
    __UPFEST_LOCATION_DIAGNOSTICS__?: LocationDiagnostic[];
  }
}

function writeLocationDiagnostic(entry: LocationDiagnostic) {
  if (!import.meta.client) return;

  try {
    const raw = window.localStorage.getItem(LOCATION_DIAGNOSTICS_KEY);
    const existing = raw ? JSON.parse(raw) : [];
    const entries = Array.isArray(existing) ? existing : [];
    const next = [...entries, entry].slice(-LOCATION_DIAGNOSTIC_LIMIT);

    window.localStorage.setItem(LOCATION_DIAGNOSTICS_KEY, JSON.stringify(next));
    window.__UPFEST_LOCATION_DIAGNOSTICS__ = next;
  } catch (error) {
    console.warn("[location] Failed to persist diagnostics", error);
  }

  const method = entry.phase.endsWith("error") ? console.warn : console.info;
  method("[location]", entry);
}

export const useLocationStore = defineStore("location", {
  state: () => ({
    permission: null as LocationPermission,
    approxRegion: "",
    latitude: null as number | null,
    longitude: null as number | null,
    city: "",
    country: "",
    source: "unknown" as LocationSource,
    accuracy: null as number | null,
    initialized: false,
    isDetecting: false,
  }),

  actions: {
    init() {
      if (this.initialized || !import.meta.client) return;

      // Restore permission from localStorage
      const savedPermission = window.localStorage.getItem(
        LOCATION_PERMISSION_KEY,
      ) as LocationPermission;
      this.permission = savedPermission;

      try {
        const rawDiagnostics = window.localStorage.getItem(
          LOCATION_DIAGNOSTICS_KEY,
        );
        window.__UPFEST_LOCATION_DIAGNOSTICS__ = rawDiagnostics
          ? JSON.parse(rawDiagnostics)
          : [];
      } catch (error) {
        console.warn("Failed to parse cached location diagnostics", error);
      }

      // Restore cached location data
      const savedData = window.localStorage.getItem(LOCATION_DATA_KEY);
      if (savedData) {
        try {
          const data: LocationData = JSON.parse(savedData);
          // Use cached data if it's less than 24 hours old
          const isFresh = Date.now() - data.timestamp < 24 * 60 * 60 * 1000;
          const isDeniedFallback =
            this.permission === "denied" && data.source === "ip-fallback";

          if (isDeniedFallback) {
            window.localStorage.removeItem(LOCATION_DATA_KEY);
          } else if (isFresh) {
            this.approxRegion = data.approxRegion;
            this.latitude = data.latitude;
            this.longitude = data.longitude;
            this.city = data.city;
            this.country = data.country;
            this.source = data.source ?? "unknown";
            this.accuracy = data.accuracy ?? null;

            this.recordLocationDiagnostic({
              phase: "cache-restored",
              source: this.source,
              lat: this.latitude,
              lon: this.longitude,
              accuracy: this.accuracy,
            });
          }
        } catch (e) {
          console.warn("Failed to parse cached location data", e);
        }
      }

      this.initialized = true;
    },

    async syncBrowserPermission() {
      if (
        !import.meta.client ||
        typeof navigator === "undefined" ||
        !("permissions" in navigator)
      ) {
        return;
      }

      try {
        const permissionStatus = await navigator.permissions.query({
          name: "geolocation" as PermissionName,
        });

        if (permissionStatus.state === "granted") {
          this.setPermission("allowed");
          return;
        }

        if (permissionStatus.state === "denied") {
          this.setPermission("denied");
        }
      } catch (error) {
        console.warn(
          "[location] Failed to sync browser geolocation permission",
          error,
        );
      }
    },

    recordLocationDiagnostic(
      entry: Omit<LocationDiagnostic, "timestamp" | "permission"> &
        Partial<Pick<LocationDiagnostic, "permission">>,
    ) {
      writeLocationDiagnostic({
        ...entry,
        timestamp: new Date().toISOString(),
        permission: entry.permission ?? this.permission,
      });
    },

    setPermission(permission: LocationPermission) {
      this.permission = permission;
      if (import.meta.client) {
        if (permission === null) {
          window.localStorage.removeItem(LOCATION_PERMISSION_KEY);
        } else {
          window.localStorage.setItem(LOCATION_PERMISSION_KEY, permission);
        }
      }
    },

    clearLocation() {
      this.approxRegion = "";
      this.latitude = null;
      this.longitude = null;
      this.city = "";
      this.country = "";
      this.source = "unknown";
      this.accuracy = null;

      if (import.meta.client) {
        window.localStorage.removeItem(LOCATION_DATA_KEY);
      }
    },

    setLocation(data: Partial<Omit<LocationData, "timestamp">>) {
      if (data.approxRegion !== undefined)
        this.approxRegion = data.approxRegion;
      if (data.latitude !== undefined) this.latitude = data.latitude;
      if (data.longitude !== undefined) this.longitude = data.longitude;
      if (data.city !== undefined) this.city = data.city;
      if (data.country !== undefined) this.country = data.country;
      if (data.source !== undefined) this.source = data.source;
      if (data.accuracy !== undefined) this.accuracy = data.accuracy;

      // Save to localStorage
      if (import.meta.client) {
        const locationData: LocationData = {
          approxRegion: this.approxRegion,
          latitude: this.latitude,
          longitude: this.longitude,
          city: this.city,
          country: this.country,
          source: this.source,
          accuracy: this.accuracy,
          timestamp: Date.now(),
        };
        window.localStorage.setItem(
          LOCATION_DATA_KEY,
          JSON.stringify(locationData),
        );
      }
    },

    async setLocationFromCoords(lat: number, lon: number) {
      // Immediately persist coordinates and set a temporary label
      this.setLocation({
        latitude: lat,
        longitude: lon,
        approxRegion: `${lat.toFixed(5)}, ${lon.toFixed(5)}`,
        source: "manual",
        accuracy: null,
      });

      // Then attempt reverse geocoding via BFF to get readable location
      try {
        const url = `/reverse-geocode?lat=${lat}&lon=${lon}`;
        const j = await $fetch<{ address?: Record<string, unknown> }>(url);
        const addr = j.address ?? {};
        const city = String(
          addr.city ?? addr.town ?? addr.village ?? addr.hamlet ?? "",
        );
        const state = addr.state || addr.county || "";
        const country = String(addr.country ?? "");
        const parts = [];
        if (city) parts.push(city);
        if (state && state !== city) parts.push(state);
        if (country) parts.push(country);
        const label =
          parts.join(", ") || `${lat.toFixed(2)}, ${lon.toFixed(2)}`;

        // Update with readable location (lat/lon already persisted)
        this.setLocation({
          approxRegion: label,
          city: city || "",
          country: country || "",
        });
      } catch (e) {
        console.warn("Reverse geocoding failed, using coordinates as label", e);
        // Coordinates already set as fallback above
      }
    },

    async requestGeolocation(options: GeolocationRequestOptions = {}) {
      if (this.isDetecting) return;

      const { fallbackToIp = true } = options;

      this.isDetecting = true;
      if (!fallbackToIp && this.source === "ip-fallback") {
        this.clearLocation();
      }
      this.recordLocationDiagnostic({
        phase: "request-start",
        source: this.source,
        highAccuracy: GEOLOCATION_OPTIONS.enableHighAccuracy,
        maximumAgeMs: GEOLOCATION_OPTIONS.maximumAge,
        timeoutMs: GEOLOCATION_OPTIONS.timeout,
        secureContext: globalThis.isSecureContext,
      });

      try {
        if (typeof navigator !== "undefined" && "geolocation" in navigator) {
          await new Promise<void>((resolve) => {
            navigator.geolocation.getCurrentPosition(
              async (pos) => {
                const lat = pos.coords.latitude;
                const lon = pos.coords.longitude;
                const accuracy = pos.coords.accuracy ?? null;

                this.setPermission("allowed");
                this.setLocation({
                  latitude: lat,
                  longitude: lon,
                  source: "browser",
                  accuracy,
                });
                this.recordLocationDiagnostic({
                  phase: "browser-success",
                  source: "browser",
                  lat,
                  lon,
                  accuracy,
                  highAccuracy: GEOLOCATION_OPTIONS.enableHighAccuracy,
                  maximumAgeMs: GEOLOCATION_OPTIONS.maximumAge,
                  timeoutMs: GEOLOCATION_OPTIONS.timeout,
                  secureContext: globalThis.isSecureContext,
                });

                // Reverse geocode via BFF
                try {
                  const url = `/reverse-geocode?lat=${lat}&lon=${lon}`;
                  const j = await $fetch<{ address?: Record<string, unknown> }>(
                    url,
                  );
                  const addr = j.address ?? {};
                  const city = String(
                    addr.city ?? addr.town ?? addr.village ?? addr.hamlet ?? "",
                  );
                  const state = addr.state || addr.county || "";
                  const country = String(addr.country ?? "");
                  const parts = [];
                  if (city) parts.push(city);
                  if (state && state !== city) parts.push(state);
                  if (country) parts.push(country);
                  const label =
                    parts.join(", ") || `${lat.toFixed(2)}, ${lon.toFixed(2)}`;

                  this.setLocation({
                    approxRegion: label,
                    city: city || "",
                    country: country || "",
                  });
                } catch {
                  this.setLocation({
                    approxRegion: `${lat.toFixed(5)}, ${lon.toFixed(5)}`,
                  });
                }

                resolve();
              },
              async (error) => {
                this.recordLocationDiagnostic({
                  phase: "browser-error",
                  source: "browser",
                  errorCode: error.code,
                  errorMessage: error.message,
                  highAccuracy: GEOLOCATION_OPTIONS.enableHighAccuracy,
                  maximumAgeMs: GEOLOCATION_OPTIONS.maximumAge,
                  timeoutMs: GEOLOCATION_OPTIONS.timeout,
                  secureContext: globalThis.isSecureContext,
                });

                if (!fallbackToIp) {
                  // Treat unresolved browser geolocation as denied so consent flow is never blocked.
                  this.setPermission("denied");
                  resolve();
                  return;
                }

                if (error.code === 1) {
                  this.setPermission("denied");
                }

                await this.fallbackDetectRegion();
                resolve();
              },
              GEOLOCATION_OPTIONS,
            );
          });
        } else {
          this.recordLocationDiagnostic({
            phase: "browser-unavailable",
            source: this.source,
            secureContext: globalThis.isSecureContext,
          });

          if (!fallbackToIp) {
            // If browser geolocation is unavailable, resolve as denied and continue.
            this.setPermission("denied");
            return;
          }

          await this.fallbackDetectRegion();
        }
      } finally {
        this.isDetecting = false;
      }
    },

    declineLocation() {
      this.setPermission("denied");
      this.clearLocation();
    },

    async fallbackDetectRegion() {
      this.recordLocationDiagnostic({
        phase: "fallback-start",
        source: "ip-fallback",
      });

      try {
        const r = await fetch("https://ipapi.co/json/");
        if (r.ok) {
          const j = await r.json();
          const city = (j.city || "").toString();
          const regionName = (j.region || j.region_code || "").toString();
          const countryName = (j.country_name || "").toString();
          const lat = Number(j.latitude ?? j.lat ?? null);
          const lon = Number(j.longitude ?? j.lon ?? null);

          const parts: string[] = [];
          if (city) parts.push(city);
          if (regionName && !parts.includes(regionName)) parts.push(regionName);
          if (countryName && !parts.includes(countryName))
            parts.push(countryName);
          const label =
            parts.join(", ") ||
            (!isNaN(lat) && !isNaN(lon)
              ? `${lat.toFixed(5)}, ${lon.toFixed(5)}`
              : "Unknown location");

          this.setLocation({
            approxRegion: label,
            latitude: !isNaN(lat) ? lat : null,
            longitude: !isNaN(lon) ? lon : null,
            city: city || "",
            country: countryName || "",
            source: "ip-fallback",
            accuracy: null,
          });
          this.recordLocationDiagnostic({
            phase: "fallback-success",
            source: "ip-fallback",
            lat: !isNaN(lat) ? lat : null,
            lon: !isNaN(lon) ? lon : null,
            accuracy: null,
          });
        } else {
          this.recordLocationDiagnostic({
            phase: "fallback-error",
            source: "ip-fallback",
            errorMessage: `ipapi returned status ${r.status}`,
          });
          this.setLocation({
            approxRegion: "Unknown location",
            source: "unknown",
            accuracy: null,
          });
        }
      } catch (e) {
        this.recordLocationDiagnostic({
          phase: "fallback-error",
          source: "ip-fallback",
          errorMessage:
            e instanceof Error ? e.message : "Unknown fallback error",
        });
        this.setLocation({
          approxRegion: "Unknown location",
          source: "unknown",
          accuracy: null,
        });
      }
    },
  },
});
